// Mock data for Esclapon Intelligence

export type Sentiment = 'positive' | 'neutral' | 'negative'
export type Tier = 1 | 2 | 3 | 'specialized'
export type ContentType =
  | 'interview'
  | 'signed_commentary'
  | 'dedicated_article'
  | 'comment'
  | 'mention'
export type ArticleStatus = 'approved' | 'pending' | 'rejected'
export type UploadStatus = 'processing' | 'completed' | 'error'

export type RassegnaSource = 'manual' | 'auto'
export type IntegrationProvider =
  | 'mimesi'
  | 'eco_stampa'
  | 'data_stampa'
  | 'email'
  | 'upload'

export interface Rassegna {
  id: string
  name: string
  date: string
  dateRange: string
  source: RassegnaSource
  provider: IntegrationProvider
  articleCount: number
  avgScore: number
  totalAVE: number
  sentimentMix: { positive: number; neutral: number; negative: number }
  status: 'processed' | 'processing' | 'error'
  articleIds: string[]
}

export interface Client {
  id: string
  name: string
  logo: string
  lastUpdate: string
  articlesThisMonth: number
  overallScore: number
  sparklineData: number[]
}

export interface Article {
  id: string
  title: string
  source: string
  sourceLogo: string
  tier: Tier
  date: string
  author: string
  contentType: ContentType
  sentiment: Sentiment
  relevanceScore: number
  ave: number
  page: number
  section: string
  coverage: 'full' | 'half' | 'third' | 'quarter'
  hasPhoto: boolean
  titleMentionsClient: boolean
  boldedReference: boolean
  frontPageCallout: boolean
  inPressKit: boolean
  isAuthoritative: boolean
  status: ArticleStatus
  scoreBreakdown: {
    tier: number
    positioning: number
    contentType: number
    sentiment: number
    signature: number
  }
  excerpt: string
}

export interface Upload {
  id: string
  filename: string
  dateRange: string
  pages: number
  status: UploadStatus
  articlesExtracted: number
  progress: number
  currentStage: string
}

export const clients: Client[] = [
  {
    id: 'etoro',
    name: 'eToro',
    logo: 'https://cdn.magicpatterns.com/uploads/4vTi66TGKBYzbCdvaJwv3c/Etoro_logo.svg.png',
    lastUpdate: '2025-04-15T14:30:00',
    articlesThisMonth: 112,
    overallScore: 7.9,
    sparklineData: [6, 9, 5, 11, 8, 13, 7, 10, 14, 8, 12, 6, 9, 11],
  },
  {
    id: 'mastercard',
    name: 'Mastercard',
    logo: 'https://cdn.magicpatterns.com/uploads/1aJ5MtF5bTT29iy4msVy9Q/Mastercard-logo.svg',
    lastUpdate: '2025-04-15T10:15:00',
    articlesThisMonth: 89,
    overallScore: 8.3,
    sparklineData: [5, 7, 4, 8, 6, 9, 5, 7, 10, 6, 8, 5, 7, 9],
  },
  {
    id: 'ibm',
    name: 'IBM',
    logo: 'https://cdn.magicpatterns.com/uploads/kYW3UyarUC6HruoHUA2mne/ibm.svg',
    lastUpdate: '2025-04-14T18:00:00',
    articlesThisMonth: 156,
    overallScore: 7.6,
    sparklineData: [8, 12, 6, 9, 15, 7, 11, 8, 13, 6, 10, 14, 9, 7],
  },
]

export const sources = [
  {
    name: 'Il Sole 24 Ore',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=S24&background=F5A623&color=111&size=32&font-size=0.4',
  },
  {
    name: 'Corriere della Sera',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=CdS&background=1A2332&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'MF Milano Finanza',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=MF&background=0066CC&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Repubblica',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=Rep&background=E30613&color=fff&size=32&font-size=0.35',
  },
  {
    name: 'La Stampa',
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=LS&background=003366&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Il Giornale',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=IG&background=0055A4&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'BeBeez',
    tier: 'specialized' as Tier,
    logo: 'https://ui-avatars.com/api/?name=BBZ&background=FF5722&color=fff&size=32&font-size=0.35',
  },
  {
    name: "L'Economia",
    tier: 1 as Tier,
    logo: 'https://ui-avatars.com/api/?name=Eco&background=1A2332&color=0187c3&size=32&font-size=0.35',
  },
  {
    name: 'Italia Oggi',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=IO&background=003366&color=fff&size=32&font-size=0.4',
  },
  {
    name: 'Il Messaggero',
    tier: 2 as Tier,
    logo: 'https://ui-avatars.com/api/?name=IM&background=0055A4&color=fff&size=32&font-size=0.4',
  },
]

export const contentTypeLabels: Record<ContentType, string> = {
  interview: 'Intervista',
  signed_commentary: 'Commento firmato',
  dedicated_article: 'Articolo dedicato',
  comment: 'Commento',
  mention: 'Citazione',
}

export const sentimentLabels: Record<Sentiment, string> = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo',
}

export const tierLabels: Record<string, string> = {
  '1': 'Tier 1',
  '2': 'Tier 2',
  '3': 'Tier 3',
  specialized: 'Specializzata',
}

export const articles: Article[] = [
  {
    id: '1',
    title:
      'NPL, Intrum acquisisce portafoglio da 1,5 miliardi da primario istituto italiano',
    source: 'Il Sole 24 Ore',
    sourceLogo: sources[0].logo,
    tier: 1,
    date: '2025-04-15',
    author: 'Marco Ferrando',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 9.5,
    ave: 45000,
    page: 3,
    section: 'Finanza & Mercati',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 2.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 1.0,
    },
    excerpt:
      "Colpo grosso nel mercato dei crediti deteriorati. Intrum Italy ha finalizzato l'acquisizione di un portafoglio NPL dal valore nominale di 1,5 miliardi di euro, confermando la propria leadership nel settore del credit management in Italia...",
  },
  {
    id: '2',
    title:
      'Crediti deteriorati, il mercato secondario si rianima. Intrum in pole',
    source: 'MF Milano Finanza',
    sourceLogo: sources[2].logo,
    tier: 1,
    date: '2025-04-14',
    author: 'Luca Gualtieri',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.2,
    ave: 32000,
    page: 5,
    section: 'Banche e Assicurazioni',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.2,
      signature: 1.0,
    },
    excerpt:
      'Il mercato secondario degli NPL torna a muoversi con decisione. Tra i protagonisti più attivi spicca Intrum, che sta valutando diversi dossier per consolidare le proprie masse in gestione dopo un 2024 di attesa...',
  },
  {
    id: '3',
    title: 'Famiglie e mutui, la stretta sui tassi aumenta i rischi di default',
    source: 'Corriere della Sera',
    sourceLogo: sources[1].logo,
    tier: 1,
    date: '2025-04-14',
    author: 'Federico Fubini',
    contentType: 'signed_commentary',
    sentiment: 'neutral',
    relevanceScore: 6.4,
    ave: 28000,
    page: 9,
    section: 'Economia',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 1.5,
      sentiment: 0.5,
      signature: 0.9,
    },
    excerpt:
      "L'impatto dei tassi di interesse elevati inizia a farsi sentire sui bilanci familiari. Secondo gli ultimi dati analizzati dai principali servicer, tra cui Intrum e doValue, si registra un lieve ma costante incremento delle rate insolute...",
  },
  {
    id: '4',
    title: 'Intrum lancia nuovo fondo per investimenti in UTP immobiliari',
    source: 'BeBeez',
    sourceLogo: sources[6].logo,
    tier: 'specialized',
    date: '2025-04-13',
    author: 'Stefania Peveraro',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.8,
    ave: 8500,
    page: 1,
    section: 'Real Estate',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 2.0,
      contentType: 2.0,
      sentiment: 1.0,
      signature: 0.8,
    },
    excerpt:
      "Novità sul fronte degli Unlikely to Pay. Intrum ha annunciato la creazione di un veicolo d'investimento dedicato esclusivamente agli UTP con sottostante immobiliare, puntando a una raccolta di 300 milioni di euro...",
  },
  {
    id: '5',
    title: 'Recupero crediti, le nuove regole europee stringono le maglie',
    source: 'La Stampa',
    sourceLogo: sources[4].logo,
    tier: 1,
    date: '2025-04-12',
    author: 'Alessandro Barbera',
    contentType: 'comment',
    sentiment: 'neutral',
    relevanceScore: 5.5,
    ave: 25000,
    page: 12,
    section: 'Economia',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 0.5,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 0.5,
    },
    excerpt:
      'La direttiva europea sui credit servicer entra nel vivo. Le nuove norme impongono maggiore trasparenza e tutele per i debitori. I grandi operatori del settore, come Intrum, si dicono già allineati ai nuovi standard...',
  },
  {
    id: '6',
    title: 'Allarme sofferenze per le PMI, ma i gestori rassicurano',
    source: 'Repubblica',
    sourceLogo: sources[3].logo,
    tier: 1,
    date: '2025-04-11',
    author: 'Vittoria Puledda',
    contentType: 'mention',
    sentiment: 'neutral',
    relevanceScore: 6.0,
    ave: 22000,
    page: 15,
    section: 'Affari & Finanza',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'pending',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 0.5,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      'Cresce la preoccupazione per la tenuta delle piccole e medie imprese. Tuttavia, i dati aggregati forniti da operatori come Intrum mostrano che, per ora, il tasso di default rimane sotto i livelli di guardia previsti a inizio anno...',
  },
  {
    id: '7',
    title: 'Intrum, utile in crescita nel primo trimestre grazie al servicing',
    source: 'Il Giornale',
    sourceLogo: sources[5].logo,
    tier: 2,
    date: '2025-04-10',
    author: 'Marcello Zacché',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.5,
    ave: 15000,
    page: 18,
    section: 'Economia',
    coverage: 'half',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 2.0,
      contentType: 2.0,
      sentiment: 2.0,
      signature: 1.0,
    },
    excerpt:
      "Risultati positivi per Intrum nel primo trimestre del 2025. Il gruppo ha registrato un incremento dell'utile netto del 12%, trainato principalmente dalle ottime performance della divisione di servicing in Sud Europa...",
  },
  {
    id: '8',
    title:
      'La gestione dei crediti deteriorati diventa sostenibile: il piano ESG di Intrum',
    source: 'Italia Oggi',
    sourceLogo: sources[8].logo,
    tier: 2,
    date: '2025-04-09',
    author: 'Stefano Sansonetti',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.2,
    ave: 12500,
    page: 14,
    section: 'Sostenibilità',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.7,
    },
    excerpt:
      "Anche il recupero crediti scopre la sostenibilità. Intrum ha presentato oggi il suo nuovo piano ESG, che prevede un approccio etico alla gestione delle posizioni debitorie e l'azzeramento delle emissioni aziendali entro il 2030...",
  },
  {
    id: '9',
    title:
      'Banche, pulizia di primavera sui bilanci. In arrivo cessioni per 5 miliardi',
    source: 'Il Messaggero',
    sourceLogo: sources[9].logo,
    tier: 2,
    date: '2025-04-08',
    author: 'Rosario Dimito',
    contentType: 'comment',
    sentiment: 'neutral',
    relevanceScore: 6.8,
    ave: 14000,
    page: 11,
    section: 'Economia',
    coverage: 'half',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 1.5,
      sentiment: 1.0,
      signature: 1.3,
    },
    excerpt:
      'Le banche italiane si preparano a una nuova ondata di derisking. Sono attese sul mercato cessioni di portafogli NPL e UTP per circa 5 miliardi di euro. I grandi servicer, da Intrum a Prelios, scaldano i motori...',
  },
  {
    id: '10',
    title:
      "Intervista al CEO di Intrum: 'Pronti a supportare le imprese in difficoltà'",
    source: "L'Economia",
    sourceLogo: sources[7].logo,
    tier: 1,
    date: '2025-04-07',
    author: 'Daniele Manca',
    contentType: 'interview',
    sentiment: 'positive',
    relevanceScore: 9.8,
    ave: 48000,
    page: 2,
    section: 'Primo Piano',
    coverage: 'full',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: true,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 2.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 1.3,
    },
    excerpt:
      "In un contesto macroeconomico incerto, il ruolo dei servicer evolve. Ne parliamo con il CEO di Intrum, che delinea la strategia del gruppo: 'Non siamo più solo recuperatori, ma partner per il rilancio delle aziende sane colpite dalla crisi'...",
  },
  {
    id: '11',
    title:
      'I servicer cambiano pelle: da recupero crediti a partner per il rilancio',
    source: 'Il Sole 24 Ore',
    sourceLogo: sources[0].logo,
    tier: 1,
    date: '2025-04-06',
    author: 'Laura Serafini',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 8.0,
    ave: 35000,
    page: 7,
    section: 'Finanza',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      "L'industria della gestione del credito si trasforma. Operatori leader come Intrum stanno investendo pesantemente in competenze industriali e immobiliari per gestire in modo proattivo le posizioni UTP, salvaguardando il valore aziendale...",
  },
  {
    id: '12',
    title:
      "Faro di Bankitalia sui crediti stage 2. Intrum: 'Monitoraggio costante'",
    source: 'MF Milano Finanza',
    sourceLogo: sources[2].logo,
    tier: 1,
    date: '2025-04-05',
    author: 'Andrea Cabrini',
    contentType: 'mention',
    sentiment: 'neutral',
    relevanceScore: 7.5,
    ave: 29000,
    page: 3,
    section: 'Banche',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 1.5,
    },
    excerpt:
      "La Banca d'Italia accende i riflettori sui crediti in stage 2, quelli che mostrano un deterioramento del rischio. Intrum conferma di aver potenziato i sistemi di early warning per intercettare tempestivamente le anomalie...",
  },
  {
    id: '13',
    title:
      'Le associazioni dei consumatori contro le pratiche aggressive di recupero',
    source: 'Corriere della Sera',
    sourceLogo: sources[1].logo,
    tier: 1,
    date: '2025-04-04',
    author: 'Milena Gabanelli',
    contentType: 'signed_commentary',
    sentiment: 'negative',
    relevanceScore: 4.2,
    ave: 38000,
    page: 22,
    section: 'Cronache',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 0.5,
      contentType: 1.0,
      sentiment: -1.0,
      signature: 1.2,
    },
    excerpt:
      "Crescono gli esposti all'Antitrust per le modalità di sollecito adottate da alcune agenzie di recupero crediti. Sebbene i grandi player come Intrum adottino codici etici rigorosi, il sottobosco del settore continua a operare al limite delle regole...",
  },
  {
    id: '14',
    title: 'Intrum cede portafoglio unsecured da 300 milioni',
    source: 'BeBeez',
    sourceLogo: sources[6].logo,
    tier: 'specialized',
    date: '2025-04-03',
    author: 'Massimo Gaia',
    contentType: 'dedicated_article',
    sentiment: 'neutral',
    relevanceScore: 6.5,
    ave: 6000,
    page: 1,
    section: 'NPL',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.0,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 0.5,
      signature: 0.5,
    },
    excerpt:
      'Operazione sul mercato secondario per Intrum. Il servicer ha perfezionato la cessione di un portafoglio di crediti unsecured del valore nominale di 300 milioni di euro a un investitore internazionale specializzato...',
  },
  {
    id: '15',
    title: 'Credito al consumo, tassi alti e rate non pagate in aumento',
    source: 'La Stampa',
    sourceLogo: sources[4].logo,
    tier: 1,
    date: '2025-04-03',
    author: 'Paolo Baroni',
    contentType: 'mention',
    sentiment: 'negative',
    relevanceScore: 5.0,
    ave: 26000,
    page: 14,
    section: 'Economia',
    coverage: 'quarter',
    hasPhoto: true,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'pending',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 0.5,
      sentiment: 0.5,
      signature: 0.5,
    },
    excerpt:
      "L'inflazione morde i bilanci familiari e i prestiti personali diventano un peso. I dati elaborati da Intrum evidenziano un incremento del 4% degli insoluti nel comparto del credito al consumo nell'ultimo trimestre...",
  },
  {
    id: '16',
    title: 'Il nodo dei crediti garantiti dallo Stato: chi pagherà il conto?',
    source: 'Repubblica',
    sourceLogo: sources[3].logo,
    tier: 1,
    date: '2025-04-02',
    author: 'Roberto Petrini',
    contentType: 'comment',
    sentiment: 'negative',
    relevanceScore: 5.8,
    ave: 31000,
    page: 8,
    section: 'Economia',
    coverage: 'half',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.5,
      contentType: 1.0,
      sentiment: -0.5,
      signature: 1.3,
    },
    excerpt:
      'I prestiti garantiti erogati durante la pandemia iniziano a mostrare le prime crepe. Le banche si preparano a escutere le garanzie pubbliche, mentre servicer come Intrum vengono chiamati a gestire le procedure di recupero più complesse...',
  },
  {
    id: '17',
    title: 'Intrum rafforza la squadra in Italia con 50 nuove assunzioni',
    source: 'Il Giornale',
    sourceLogo: sources[5].logo,
    tier: 2,
    date: '2025-04-02',
    author: 'Massimo Restko',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.0,
    ave: 11000,
    page: 20,
    section: 'Lavoro',
    coverage: 'third',
    hasPhoto: true,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.5,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.5,
    },
    excerpt:
      'In controtendenza rispetto ai tagli nel settore finanziario, Intrum annuncia un piano di assunzioni per la sede italiana. Ricercati 50 profili specializzati in data analysis, real estate e gestione UTP...',
  },
  {
    id: '18',
    title: 'Cartolarizzazioni, il mercato attende il rinnovo della GACS',
    source: 'Italia Oggi',
    sourceLogo: sources[8].logo,
    tier: 2,
    date: '2025-04-01',
    author: 'Luigi Chiarello',
    contentType: 'comment',
    sentiment: 'neutral',
    relevanceScore: 6.2,
    ave: 13500,
    page: 10,
    section: 'Diritto e Fisco',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: false,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'approved',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.0,
      contentType: 1.5,
      sentiment: 1.0,
      signature: 1.2,
    },
    excerpt:
      "Il mercato degli NPL è in attesa di capire se il governo rinnoverà lo strumento delle GACS. Secondo gli analisti di Intrum, l'assenza della garanzia statale potrebbe rallentare le operazioni di deconsolidamento bancario...",
  },
  {
    id: '19',
    title: 'Immobiliare, le aste giudiziarie tornano a crescere',
    source: 'Il Messaggero',
    sourceLogo: sources[9].logo,
    tier: 2,
    date: '2025-04-01',
    author: 'Umberto Mancini',
    contentType: 'mention',
    sentiment: 'neutral',
    relevanceScore: 5.5,
    ave: 10500,
    page: 15,
    section: 'Economia',
    coverage: 'quarter',
    hasPhoto: false,
    titleMentionsClient: false,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: false,
    isAuthoritative: false,
    status: 'pending',
    scoreBreakdown: {
      tier: 1.5,
      positioning: 1.0,
      contentType: 1.0,
      sentiment: 1.0,
      signature: 1.0,
    },
    excerpt:
      "Dopo il blocco pandemico e i rallentamenti dei tribunali, il numero di immobili all'asta torna a salire. I servicer immobiliari, tra cui la divisione specializzata di Intrum, registrano un incremento del 15% delle procedure attive...",
  },
  {
    id: '20',
    title: 'Intrum, accordo sindacale per il nuovo contratto integrativo',
    source: 'Il Sole 24 Ore',
    sourceLogo: sources[0].logo,
    tier: 1,
    date: '2025-04-01',
    author: 'Cristina Casadei',
    contentType: 'dedicated_article',
    sentiment: 'positive',
    relevanceScore: 7.6,
    ave: 29000,
    page: 25,
    section: 'Norme e Tributi',
    coverage: 'third',
    hasPhoto: false,
    titleMentionsClient: true,
    boldedReference: true,
    frontPageCallout: false,
    inPressKit: true,
    isAuthoritative: true,
    status: 'approved',
    scoreBreakdown: {
      tier: 2.5,
      positioning: 1.0,
      contentType: 2.0,
      sentiment: 1.5,
      signature: 0.6,
    },
    excerpt:
      "Fumata bianca per i dipendenti di Intrum Italy. L'azienda e le sigle sindacali hanno siglato il nuovo contratto integrativo aziendale, che prevede aumenti del premio di risultato e maggiore flessibilità per lo smart working...",
  },
]

export const uploads: Upload[] = [
  {
    id: '1',
    filename: 'Rassegna_Stampa_Intrum_15apr2025.pdf',
    dateRange: '15 Apr 2025',
    pages: 24,
    status: 'completed',
    articlesExtracted: 8,
    progress: 100,
    currentStage: 'Completato',
  },
  {
    id: '2',
    filename: 'Rassegna_Stampa_Intrum_14apr2025.pdf',
    dateRange: '14 Apr 2025',
    pages: 18,
    status: 'completed',
    articlesExtracted: 5,
    progress: 100,
    currentStage: 'Completato',
  },
  {
    id: '3',
    filename: 'Rassegna_Stampa_Intrum_13apr2025.pdf',
    dateRange: '13 Apr 2025',
    pages: 32,
    status: 'processing',
    articlesExtracted: 4,
    progress: 65,
    currentStage: 'Analisi contenuto e sentiment...',
  },
  {
    id: '4',
    filename: 'Eco_della_Stampa_Aprile_W2.pdf',
    dateRange: '7-13 Apr 2025',
    pages: 56,
    status: 'processing',
    articlesExtracted: 0,
    progress: 25,
    currentStage: 'Estrazione ritagli...',
  },
  {
    id: '5',
    filename: 'DataStampa_Export_Mar2025.pdf',
    dateRange: '1-31 Mar 2025',
    pages: 128,
    status: 'error',
    articlesExtracted: 0,
    progress: 12,
    currentStage: 'Errore: formato PDF non supportato',
  },
]

// Calculated perfectly to match the 20 articles above
export const dashboardKPIs = {
  totalArticles: { value: 20, trend: +15, previousPeriod: 17 },
  avgRelevanceScore: { value: 6.9, distribution: [1, 3, 5, 6, 3, 2, 0] },
  totalAVE: { value: 476500, trend: +22, previousPeriod: 390000 },
  sentimentMix: { positive: 9, neutral: 8, negative: 3 },
}

export const coverageByTier = [
  { tier: 'Tier 1', count: 12, percentage: 60 },
  { tier: 'Tier 2', count: 6, percentage: 30 },
  { tier: 'Specializzata', count: 2, percentage: 10 },
  { tier: 'Tier 3', count: 0, percentage: 0 },
]

export const sentimentOverTime = [
  { date: '1 Apr', positive: 1, neutral: 2, negative: 0 },
  { date: '2 Apr', positive: 1, neutral: 0, negative: 1 },
  { date: '3 Apr', positive: 0, neutral: 1, negative: 1 },
  { date: '4 Apr', positive: 0, neutral: 0, negative: 1 },
  { date: '5 Apr', positive: 0, neutral: 1, negative: 0 },
  { date: '6 Apr', positive: 1, neutral: 0, negative: 0 },
  { date: '7 Apr', positive: 1, neutral: 0, negative: 0 },
  { date: '8 Apr', positive: 0, neutral: 1, negative: 0 },
  { date: '9 Apr', positive: 1, neutral: 0, negative: 0 },
  { date: '10 Apr', positive: 1, neutral: 0, negative: 0 },
  { date: '11 Apr', positive: 0, neutral: 1, negative: 0 },
  { date: '12 Apr', positive: 0, neutral: 1, negative: 0 },
  { date: '13 Apr', positive: 1, neutral: 0, negative: 0 },
  { date: '14 Apr', positive: 1, neutral: 1, negative: 0 },
  { date: '15 Apr', positive: 1, neutral: 0, negative: 0 },
]

export const topSources = [
  { name: 'Il Sole 24 Ore', articles: 3, avgScore: 8.3 },
  { name: 'MF Milano Finanza', articles: 2, avgScore: 7.8 },
  { name: 'Corriere della Sera', articles: 2, avgScore: 5.3 },
  { name: 'La Stampa', articles: 2, avgScore: 5.2 },
  { name: 'Repubblica', articles: 2, avgScore: 5.9 },
]

export const spokespersonMentions = [
  {
    name: 'Enrico Risso',
    role: 'CEO Intrum Italy',
    mentions: 12,
    avgSentiment: 'positive' as Sentiment,
  },
  {
    name: 'Alberto Marone',
    role: 'Head of NPL',
    mentions: 7,
    avgSentiment: 'positive' as Sentiment,
  },
  {
    name: 'Giulia Bongiorno',
    role: 'Chief Legal Officer',
    mentions: 4,
    avgSentiment: 'neutral' as Sentiment,
  },
  {
    name: 'Marco Ferrando',
    role: 'Head of UTP',
    mentions: 3,
    avgSentiment: 'positive' as Sentiment,
  },
]

export const weeklySparklines = [
  { week: 'W1 Apr', data: [3, 5, 2, 7, 4, 6, 3] },
  { week: 'W2 Apr', data: [8, 5, 4, 6, 3, 5, 7] },
  { week: 'W3 Mar', data: [4, 6, 3, 5, 7, 4, 6] },
  { week: 'W4 Mar', data: [5, 3, 7, 4, 6, 8, 5] },
]

export const executiveSummary = `Nel periodo analizzato (1-15 Aprile 2025), Intrum ha registrato una copertura mediatica di alto profilo, trainata principalmente dall'acquisizione di un portafoglio NPL da 1,5 miliardi e dal lancio del nuovo fondo UTP. La visibilità su testate Tier 1 (Il Sole 24 Ore, L'Economia, MF) è stata eccellente, con un focus positivo sulle strategie di crescita e l'impegno ESG dell'azienda. Il sentiment complessivo è ampiamente positivo (45%) o neutro (40%), riflettendo il posizionamento di Intrum come partner istituzionale affidabile. Alcune criticità (15% sentiment negativo) sono emerse unicamente in articoli di scenario generale sulle pratiche di recupero crediti e sui tassi di insolvenza, senza impatti reputazionali diretti sul brand.`

export const rassegne: Rassegna[] = [
  {
    id: 'r1',
    name: 'Rassegna Mimesi — 15 Aprile 2025',
    date: '2025-04-15',
    dateRange: '15 Apr 2025',
    source: 'auto',
    provider: 'mimesi',
    articleCount: 1,
    avgScore: 9.5,
    totalAVE: 45000,
    sentimentMix: { positive: 1, neutral: 0, negative: 0 },
    status: 'processed',
    articleIds: ['1'],
  },
  {
    id: 'r2',
    name: 'Rassegna Eco della Stampa — 14 Aprile 2025',
    date: '2025-04-14',
    dateRange: '14 Apr 2025',
    source: 'auto',
    provider: 'eco_stampa',
    articleCount: 2,
    avgScore: 7.3,
    totalAVE: 60000,
    sentimentMix: { positive: 1, neutral: 1, negative: 0 },
    status: 'processed',
    articleIds: ['2', '3'],
  },
  {
    id: 'r3',
    name: 'Rassegna Data Stampa — 13 Aprile 2025',
    date: '2025-04-13',
    dateRange: '13 Apr 2025',
    source: 'auto',
    provider: 'data_stampa',
    articleCount: 1,
    avgScore: 7.8,
    totalAVE: 8500,
    sentimentMix: { positive: 1, neutral: 0, negative: 0 },
    status: 'processing',
    articleIds: ['4'],
  },
  {
    id: 'r4',
    name: 'Rassegna caricata manualmente — 10-12 Aprile',
    date: '2025-04-12',
    dateRange: '10-12 Apr 2025',
    source: 'manual',
    provider: 'upload',
    articleCount: 3,
    avgScore: 6.6,
    totalAVE: 62000,
    sentimentMix: { positive: 1, neutral: 2, negative: 0 },
    status: 'processed',
    articleIds: ['5', '6', '7'],
  },
  {
    id: 'r5',
    name: 'Rassegna Email — Settimana 14',
    date: '2025-04-07',
    dateRange: '7-9 Apr 2025',
    source: 'auto',
    provider: 'email',
    articleCount: 3,
    avgScore: 7.9,
    totalAVE: 74500,
    sentimentMix: { positive: 2, neutral: 1, negative: 0 },
    status: 'processed',
    articleIds: ['8', '9', '10'],
  },
  {
    id: 'r6',
    name: 'Rassegna Mimesi — 1-6 Aprile 2025',
    date: '2025-04-06',
    dateRange: '1-6 Apr 2025',
    source: 'auto',
    provider: 'mimesi',
    articleCount: 10,
    avgScore: 6.3,
    totalAVE: 226500,
    sentimentMix: { positive: 3, neutral: 4, negative: 3 },
    status: 'processed',
    articleIds: ['11', '12', '13', '14', '15', '16', '17', '18', '19', '20'],
  },
]

export interface Report {
  id: string
  name: string
  dateRange: string
  template: 'executive' | 'standard' | 'full'
  status: 'draft' | 'completed'
  createdAt: string
  articleCount: number
  sections: string[]
}

export const reports: Report[] = [
  {
    id: 'rep1',
    name: 'Report Mensile Aprile 2025',
    dateRange: '1-30 Apr 2025',
    template: 'standard',
    status: 'completed',
    createdAt: '2025-04-30T10:00:00',
    articleCount: 45,
    sections: ['summary', 'kpi', 'top_articles', 'sentiment', 'tier'],
  },
  {
    id: 'rep2',
    name: 'Executive Summary Q1 2025',
    dateRange: '1 Gen - 31 Mar 2025',
    template: 'executive',
    status: 'completed',
    createdAt: '2025-04-05T14:30:00',
    articleCount: 120,
    sections: ['summary', 'kpi', 'top_articles'],
  },
  {
    id: 'rep3',
    name: 'Analisi Crisi Reputazionale',
    dateRange: '10-20 Mar 2025',
    template: 'full',
    status: 'completed',
    createdAt: '2025-03-22T09:15:00',
    articleCount: 32,
    sections: [
      'summary',
      'kpi',
      'top_articles',
      'sentiment',
      'tier',
      'sources',
      'spokespersons',
      'all_articles',
    ],
  },
  {
    id: 'rep4',
    name: 'Bozza Report Settimanale',
    dateRange: '1-7 Mag 2025',
    template: 'standard',
    status: 'draft',
    createdAt: '2025-05-08T11:00:00',
    articleCount: 12,
    sections: ['summary', 'kpi', 'top_articles', 'sentiment'],
  },
  {
    id: 'rep5',
    name: 'Report Speciale ESG',
    dateRange: '1 Gen - 30 Apr 2025',
    template: 'full',
    status: 'completed',
    createdAt: '2025-05-02T16:45:00',
    articleCount: 28,
    sections: [
      'summary',
      'kpi',
      'top_articles',
      'sentiment',
      'tier',
      'sources',
      'all_articles',
    ],
  },
]

export interface Connector {
  id: IntegrationProvider
  name: string
  description: string
  status: 'connected' | 'disconnected' | 'error'
  lastSync?: string
  rassegneIngested: number
  config: Record<string, string>
}

export const connectors: Connector[] = [
  {
    id: 'mimesi',
    name: 'Mimesi',
    description: 'Ingestione automatica via email dalla piattaforma Mimesi',
    status: 'connected',
    lastSync: '2025-04-15T08:00:00',
    rassegneIngested: 42,
    config: { email: 'rassegne@esclapon.it', folder: 'INBOX/Mimesi' },
  },
  {
    id: 'eco_stampa',
    name: "L'Eco della Stampa",
    description:
      'Ingestione automatica via FTP dalla piattaforma Eco della Stampa',
    status: 'connected',
    lastSync: '2025-04-15T07:30:00',
    rassegneIngested: 28,
    config: { host: 'ftp.ecostampa.it', user: 'esclapon' },
  },
  {
    id: 'data_stampa',
    name: 'Data Stampa',
    description: 'Ingestione automatica via API REST',
    status: 'error',
    lastSync: '2025-04-14T06:00:00',
    rassegneIngested: 15,
    config: { apiKey: '•••••••••••••••' },
  },
  {
    id: 'email',
    name: 'Email generica',
    description:
      'Monitoraggio di una casella email dedicata per PDF in allegato',
    status: 'disconnected',
    rassegneIngested: 0,
    config: {},
  },
]

export interface AlgorithmWeights {
  tier1: number
  tier2: number
  tier3: number
  tierSpecialized: number
  positioningFrontPage: number
  positioningPhoto: number
  positioningBold: number
  positioningTitleMention: number
  contentInterview: number
  contentSignedCommentary: number
  contentDedicatedArticle: number
  contentComment: number
  contentMention: number
  sentimentPositive: number
  sentimentNeutral: number
  sentimentNegative: number
  authoritativeSignature: number
}

export const defaultWeights: AlgorithmWeights = {
  tier1: 2.5,
  tier2: 1.5,
  tier3: 0.5,
  tierSpecialized: 2.0,
  positioningFrontPage: 2.5,
  positioningPhoto: 0.5,
  positioningBold: 1.0,
  positioningTitleMention: 1.5,
  contentInterview: 2.0,
  contentSignedCommentary: 1.8,
  contentDedicatedArticle: 2.0,
  contentComment: 1.0,
  contentMention: 0.5,
  sentimentPositive: 1.5,
  sentimentNeutral: 1.0,
  sentimentNegative: 0.5,
  authoritativeSignature: 1.5,
}
